// Publicacoes recorrentes: leitura via RLS, escrita via RPC SECURITY DEFINER.
// A proxima execucao e calculada aqui com a mesma funcao do bot (shared).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  nextOccurrence,
  normalizeRecurrenceRule,
  serializeRecurrenceRule,
  type RecurrenceRule,
} from "@lucralink/shared";
import { supabase } from "@/lib/supabase";
import type { CreateScheduledPostDeliveryInput } from "@/lib/hooks/useScheduledPosts";
import { mapRpcError } from "@/lib/rpc-error";

export type RecurringStatus = "active" | "paused" | "finished";

export interface RecurringPost {
  id: string;
  bodyText: string;
  imageUrl: string | null;
  linkId: string | null;
  deliveries: CreateScheduledPostDeliveryInput[];
  rule: RecurrenceRule | null;
  startsAt: string;
  endsAt: string | null;
  status: RecurringStatus;
  pauseReason: string | null;
  lastSkipReason: string | null;
  lastSkipAt: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  occurrencesCount: number;
}

interface RecurringRow {
  id: string;
  body_text: string;
  image_url: string | null;
  link_id: string | null;
  deliveries: unknown;
  rule: unknown;
  starts_at: string;
  ends_at: string | null;
  status: string;
  pause_reason: string | null;
  last_skip_reason?: string | null;
  last_skip_at?: string | null;
  next_run_at: string | null;
  last_run_at: string | null;
  occurrences_count: number;
}

export const RECURRING_KEY = ["recurring-posts"] as const;

function toDeliveries(raw: unknown): CreateScheduledPostDeliveryInput[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const r = item as Record<string, unknown>;
    const channel = r["channel"];
    if ((channel !== "telegram" && channel !== "whatsapp") || typeof r["targetId"] !== "string") return [];
    return [{ channel, targetId: r["targetId"], label: typeof r["label"] === "string" ? r["label"] : null }];
  });
}

export function mapRecurringRow(row: RecurringRow): RecurringPost {
  return {
    id: row.id,
    bodyText: row.body_text,
    imageUrl: row.image_url,
    linkId: row.link_id,
    deliveries: toDeliveries(row.deliveries),
    rule: normalizeRecurrenceRule(row.rule),
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    status: row.status === "paused" || row.status === "finished" ? row.status : "active",
    pauseReason: row.pause_reason,
    lastSkipReason: row.last_skip_reason ?? null,
    lastSkipAt: row.last_skip_at ?? null,
    nextRunAt: row.next_run_at,
    lastRunAt: row.last_run_at,
    occurrencesCount: row.occurrences_count ?? 0,
  };
}

async function fetchRecurring(): Promise<RecurringPost[]> {
  const { data, error } = await supabase
    .from("recurring_posts")
    .select("id, body_text, image_url, link_id, deliveries, rule, starts_at, ends_at, status, pause_reason, last_skip_reason, last_skip_at, next_run_at, last_run_at, occurrences_count")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as RecurringRow[]).map(mapRecurringRow);
}

export function useRecurringPosts() {
  return useQuery<RecurringPost[], Error>({ queryKey: RECURRING_KEY, queryFn: fetchRecurring, staleTime: 15_000 });
}

/** Proxima execucao a partir de agora (null = a regra nao tem mais ocorrencias). */
export function computeNextRun(rule: RecurrenceRule, startsAt: Date, endsAt: Date | null, now: Date = new Date()): Date | null {
  return nextOccurrence({ rule, startsAt, endsAt }, new Date(now.getTime() - 1));
}

export interface SaveRecurringInput {
  id: string | null;
  bodyText: string;
  imageUrl: string | null;
  linkId: string | null;
  deliveries: CreateScheduledPostDeliveryInput[];
  rule: RecurrenceRule;
  startsAt: Date;
  endsAt: Date | null;
}

const ERROR_LABELS: Readonly<Record<string, string>> = {
  scheduled_posts_plan_required: "Publicações recorrentes são do plano PRO.",
  recurrence_rule_invalid: "Regra de recorrência inválida. Confira dias, horários ou intervalo.",
  recurrence_end_before_start: "A data de fim precisa ser depois do início.",
  recurrence_next_run_invalid: "Essa regra não tem nenhuma publicação futura dentro do período.",
  recurring_posts_limit_reached: "Limite de 20 publicações recorrentes atingido.",
  deliveries_required: "Escolha pelo menos um destino.",
  deliveries_limit_exceeded: "Máximo de 10 destinos por publicação.",
  delivery_target_not_owned: "Um dos destinos não está mais conectado.",
  body_text_required: "Escreva o texto da publicação.",
  image_url_invalid: "URL da imagem inválida (use http ou https).",
};

/** Mensagem pt-BR para o erro da RPC. */
export function recurringErrorMessage(error: unknown): string {
  return mapRpcError(error, ERROR_LABELS, "Não foi possível salvar a publicação recorrente.");
}

async function saveRecurring(input: SaveRecurringInput): Promise<void> {
  const next = computeNextRun(input.rule, input.startsAt, input.endsAt);
  if (!next) throw new Error(recurringErrorMessage("recurrence_next_run_invalid"));
  const { error } = await supabase.rpc("upsert_recurring_post", {
    p_id: input.id,
    p_body_text: input.bodyText,
    p_image_url: input.imageUrl,
    p_link_id: input.linkId,
    p_deliveries: input.deliveries,
    p_rule: serializeRecurrenceRule(input.rule),
    p_starts_at: input.startsAt.toISOString(),
    p_ends_at: input.endsAt ? input.endsAt.toISOString() : null,
    p_next_run_at: next.toISOString(),
  });
  if (error) throw new Error(recurringErrorMessage(error));
}

export function useSaveRecurringPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveRecurring,
    onSuccess: () => qc.invalidateQueries({ queryKey: RECURRING_KEY }),
  });
}

async function setStatus(post: RecurringPost, status: "active" | "paused"): Promise<void> {
  let next: Date | null = null;
  if (status === "active") {
    if (!post.rule) throw new Error(recurringErrorMessage("recurrence_rule_invalid"));
    next = computeNextRun(post.rule, new Date(post.startsAt), post.endsAt ? new Date(post.endsAt) : null);
    if (!next) throw new Error(recurringErrorMessage("recurrence_next_run_invalid"));
  }
  const { error } = await supabase.rpc("set_recurring_post_status", {
    p_id: post.id,
    p_status: status,
    p_next_run_at: next ? next.toISOString() : null,
  });
  if (error) throw new Error(recurringErrorMessage(error));
}

export function useSetRecurringStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ post, status }: { post: RecurringPost; status: "active" | "paused" }) => setStatus(post, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: RECURRING_KEY }),
  });
}

export function useDeleteRecurringPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("delete_recurring_post", { p_id: id });
      if (error) throw new Error(recurringErrorMessage(error));
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: RECURRING_KEY });
      await qc.invalidateQueries({ queryKey: ["scheduled-posts"] });
    },
  });
}
